import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface CreateShortlistDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateShortlistDialog({ 
  isOpen, 
  onClose 
}: CreateShortlistDialogProps) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  
  // Create shortlist mutation
  const createShortlistMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const response = await apiRequest("POST", "/api/shortlists/", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Shortlist created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      
      // Reset form and close dialog
      setName("");
      setDescription("");
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create shortlist",
        variant: "destructive",
      });
    }
  });
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Shortlist name is required",
        variant: "destructive",
      });
      return;
    }
    
    createShortlistMutation.mutate({ name, description });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Shortlist</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="shortlist-name" className="block text-sm font-medium text-neutral-700 mb-1">
                Shortlist Name*
              </label>
              <Input
                id="shortlist-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Top Forwards, International Prospects"
                required
              />
            </div>
            
            <div>
              <label htmlFor="shortlist-description" className="block text-sm font-medium text-neutral-700 mb-1">
                Description (Optional)
              </label>
              <Textarea
                id="shortlist-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes about this shortlist"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={createShortlistMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={!name.trim() || createShortlistMutation.isPending}
            >
              {createShortlistMutation.isPending ? "Creating..." : "Create Shortlist"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
